import { Link, useParams } from 'react-router';

import { ROUTES } from '#/config/routes';
import { fetchStockIssueDetail } from '#/api/imsMgmt/receipts/stockIssue/fetchDetail';
import { updateStockIssueRemark } from '#/api/imsMgmt/receipts/stockIssue/updateRemark';
import { Button } from '#/modules/commonComponents/button';
import PageShell from '#/modules/infra/layout/PageShell';
import useDocumentRemarkSave from '#/hooks/useDocumentRemarkSave';
import DocumentRemarkEditForm from '#/modules/imsMgmt/receipts/shared/DocumentRemarkEditForm';
import StockIssueMainInfoCard from '#/modules/imsMgmt/receipts/stockIssue/components/StockIssueMainInfoCard';
import {
  getStockIssueApiErrorMessage,
  isStockIssueVersionConflict,
} from '#/modules/imsMgmt/receipts/stockIssue/helper/apiError';
import { canEditStockIssueRemark } from '#/modules/imsMgmt/receipts/stockIssue/helper/issueStatus';
import {
  stockIssueDetailQueryKey,
  stockIssueListQueryKeyPrefix,
} from '#/utils/queryKeys';

const StockIssueRemarkPage = () => {
  const { id } = useParams();

  const {
    detail,
    detailQuery,
    isPending,
    saveRemark,
  } = useDocumentRemarkSave({
    id,
    fetchDetail: fetchStockIssueDetail,
    updateRemark: updateStockIssueRemark,
    detailQueryKey: stockIssueDetailQueryKey,
    listQueryKeyPrefix: stockIssueListQueryKeyPrefix,
    getErrorMessage: (error) => getStockIssueApiErrorMessage(error, '更新備註失敗'),
    isVersionConflict: isStockIssueVersionConflict,
    afterSaveNavigateTo: ROUTES.stockIssueDetail(id),
  });

  const isLoading = detailQuery.isLoading;
  const isError = detailQuery.isError;
  const remarkAllowed = detail ? canEditStockIssueRemark(detail.status) : false;

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-4xl space-y-4">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">編輯出庫備註</span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.stockIssueDetail(id)}>返回詳情</Link>
            </Button>
          </div>

          {isLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {isError ? (
            <p className="text-sm text-destructive">
              {getStockIssueApiErrorMessage(detailQuery.error, '找不到出庫單')}
            </p>
          ) : null}

          {detail && !remarkAllowed ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">此狀態不可編輯備註。</p>
              <Button type="button" asChild>
                <Link to={ROUTES.stockIssueDetail(id)}>前往詳情</Link>
              </Button>
            </div>
          ) : null}

          {detail && remarkAllowed ? (
            <>
              <StockIssueMainInfoCard detail={detail} />
              <DocumentRemarkEditForm
                initialRemark={detail.remark ?? ''}
                cancelTo={ROUTES.stockIssueDetail(id)}
                isPending={isPending}
                onSave={saveRemark}
              />
            </>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default StockIssueRemarkPage;
